import { useState } from 'react'
import { Button, InputNumber, Space, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { money } from '../inputSanityModel'
import { MAX_ENTITIES } from '../constants'
import {
  BuilderSnapshot,
  CapacityDoor,
  CrewCapacity,
  previewRateForDoor
} from '../crewCapacityModel'
import './CrewCapacityCard.css'

const { Text } = Typography

type Props = {
  capacity: CrewCapacity
  snapshot: BuilderSnapshot
  onApplyCrew: (crewCount: number, hoursPerWeek: number | null) => void
  onApplyGoal: (salesGoal: number) => void
  onApplySplit: (ownShare: number, subcontracted: number) => void
}

const rateText = (rate: number): string => `$${rate.toFixed(2)}`

const CrewCapacityCard = ({ capacity, snapshot, onApplyCrew, onApplyGoal, onApplySplit }: Props) => {
  const seedCrew = capacity.crewNeeded ?? capacity.crewCount
  const seedHours = snapshot.seasonData.hoursPerWeek
  const seedGoal = capacity.goalSupported ?? capacity.goal
  const seedOwnShare = capacity.goalSupported ?? Math.round(capacity.goal / 2)

  const [door, setDoor] = useState<CapacityDoor>('choose')
  const [dismissed, setDismissed] = useState(false)
  const [crewDraft, setCrewDraft] = useState<number | null>(seedCrew)
  const [hoursDraft, setHoursDraft] = useState<number | null>(seedHours)
  const [goalDraft, setGoalDraft] = useState<number | null>(seedGoal)
  const [ownDraft, setOwnDraft] = useState<number | null>(seedOwnShare)

  const openDoor = (next: CapacityDoor) => {
    setCrewDraft(seedCrew)
    setHoursDraft(seedHours)
    setGoalDraft(seedGoal)
    setOwnDraft(seedOwnShare)
    setDoor(next)
  }

  if (dismissed) {
    return null
  }

  const subcontracted = Math.max(capacity.goal - (ownDraft ?? 0), 0)
  const keepingHours = hoursDraft === null || hoursDraft < 1

  const preview = previewRateForDoor(snapshot, door, {
    crew: crewDraft,
    hours: hoursDraft,
    goal: goalDraft,
    ownShare: ownDraft
  })

  const crewLabel = capacity.crewCount === 1 ? '1 field crew' : `${capacity.crewCount} field crew`

  const renderPreview = () => {
    if (preview === null || !isFinite(preview)) {
      return null
    }
    return (
      <div className="crew-capacity-preview">
        <div>
          Estimated rate: <span className="crew-capacity-was">{rateText(capacity.rate)}</span>
          {' → '}
          <span className="crew-capacity-now">{rateText(preview)}/hr</span>
        </div>
        <Text type="secondary" className="crew-capacity-footnote">
          Still an estimate. Your trucks, trailers and machines aren&apos;t in yet.
        </Text>
      </div>
    )
  }

  const backToDoors = (
    <Button type="text" size="small" onClick={() => setDoor('choose')}>
      Back
    </Button>
  )

  return (
    <div className="crew-capacity-card">
      <button
        type="button"
        className="crew-capacity-dismiss"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>

      {door === 'choose' && (
        <>
          <div className="crew-capacity-heading">
            <FontAwesomeIcon icon={faCircleExclamation} />
            <h4>Let&apos;s check your crew against your goal</h4>
          </div>

          <p>
            You&apos;re going for {money(capacity.goal)} with {crewLabel}. Across your season
            that&apos;s about {Math.round(capacity.billableHours).toLocaleString('en-US')} billable
            hours between them.
          </p>
          <p>
            Covering {money(capacity.goal)} out of{' '}
            {Math.round(capacity.billableHours).toLocaleString('en-US')} hours puts your estimated
            rate at about <strong>{rateText(capacity.rate)}/hr</strong>.
          </p>

          <p className="crew-capacity-question">Which one is off?</p>

          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <button type="button" className="crew-capacity-door" onClick={() => openDoor('crew')}>
              <span className="crew-capacity-door-title">I need more crew than that</span>
              <span className="crew-capacity-door-detail">
                {capacity.crewNeeded === null
                  ? `${capacity.crewCount} cannot cover ${money(capacity.goal)}`
                  : `About ${capacity.crewNeeded} in the field delivers ${money(capacity.goal)}`}
              </span>
            </button>

            <button type="button" className="crew-capacity-door" onClick={() => openDoor('goal')}>
              <span className="crew-capacity-door-title">The goal isn&apos;t right</span>
              <span className="crew-capacity-door-detail">
                {capacity.goalSupported === null
                  ? 'Set a goal this crew can carry'
                  : `${capacity.crewCount} crew supports about ${money(capacity.goalSupported)}`}
              </span>
            </button>

            <button
              type="button"
              className="crew-capacity-door crew-capacity-door-highlighted"
              onClick={() => openDoor('split')}
            >
              <span className="crew-capacity-door-title">Both are right. I sub most of it out</span>
              <span className="crew-capacity-door-detail">
                Your crew isn&apos;t doing all {money(capacity.goal)} of the work
              </span>
            </button>
          </Space>

          <p className="crew-capacity-footnote">
            Nothing here is locked. Pick the one that&apos;s true and we&apos;ll do the math.
          </p>
        </>
      )}

      {door === 'crew' && (
        <>
          <h4>How much crew have you got?</h4>
          <p>
            {capacity.crewNeeded === null
              ? `${money(capacity.goal)} needs more people in the field than that.`
              : `It takes about ${capacity.crewNeeded} in the field to deliver ${money(capacity.goal)} in a season your length.`}
          </p>

          <Space size="middle" wrap>
            <label className="crew-capacity-field">
              <span>Field crew</span>
              <InputNumber min={1} max={MAX_ENTITIES} value={crewDraft} onChange={setCrewDraft} />
            </label>
            <label className="crew-capacity-field">
              <span>
                Hours a week each <span className="crew-capacity-optional">optional</span>
              </span>
              <InputNumber min={1} value={hoursDraft} onChange={setHoursDraft} />
            </label>
          </Space>

          {renderPreview()}

          <Space size="small">
            <Button
              type="primary"
              disabled={crewDraft === null || crewDraft < 1}
              onClick={() => onApplyCrew(crewDraft!, hoursDraft)}
            >
              Use this
            </Button>
            {backToDoors}
          </Space>

          <p className="crew-capacity-footnote">
            {keepingHours
              ? "Hours left blank, so everyone keeps the week they've already got. "
              : ''}
            You can set what you pay each of them on the roster below.
          </p>
        </>
      )}

      {door === 'goal' && (
        <>
          <h4>What are you going for this year?</h4>
          <p>
            {capacity.crewCount} crew across your season comes to about{' '}
            {Math.round(capacity.billableHours).toLocaleString('en-US')} billable hours.
            {capacity.goalSupported !== null && ` That carries around ${money(capacity.goalSupported)}.`}
          </p>

          <label className="crew-capacity-field">
            <span>Revenue goal</span>
            <InputNumber
              prefix="$"
              min={0}
              value={goalDraft}
              onChange={setGoalDraft}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
              style={{ width: '180px' }}
            />
          </label>

          {renderPreview()}

          <Space size="small">
            <Button
              type="primary"
              disabled={goalDraft === null || goalDraft <= 0}
              onClick={() => onApplyGoal(goalDraft!)}
            >
              Use this
            </Button>
            {backToDoors}
          </Space>

          <p className="crew-capacity-footnote">
            This replaces the {money(capacity.goal)} you put in on the first step.
          </p>
        </>
      )}

      {door === 'split' && (
        <>
          <h4>Split your {money(capacity.goal)}</h4>
          <p>
            Fill in either box and we&apos;ll work out the other. Your rate only has to cover the
            work your own crew does.
          </p>

          <Space size="middle" wrap>
            <label className="crew-capacity-field">
              <span>Your own crew does</span>
              <InputNumber
                prefix="$"
                min={0}
                max={capacity.goal}
                value={ownDraft}
                onChange={setOwnDraft}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                style={{ width: '160px' }}
              />
            </label>
            <label className="crew-capacity-field">
              <span>You sub out</span>
              <InputNumber
                prefix="$"
                min={0}
                max={capacity.goal}
                value={subcontracted}
                onChange={(value) => setOwnDraft(Math.max(capacity.goal - (value ?? 0), 0))}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                style={{ width: '160px' }}
              />
            </label>
          </Space>

          {renderPreview()}

          <Space size="small">
            <Button
              type="primary"
              disabled={ownDraft === null || ownDraft <= 0}
              onClick={() => onApplySplit(ownDraft!, subcontracted)}
            >
              Use this
            </Button>
            {backToDoors}
          </Space>

          <p className="crew-capacity-footnote">
            <strong>Your crew&apos;s number</strong> helps us estimate your overhead and material
            expenses, so it impacts your rate. <strong>The subbed number</strong> goes on your budget
            as a subcontractor line. It counts toward your revenue and leaves your rate alone.
          </p>
        </>
      )}
    </div>
  )
}

export default CrewCapacityCard
